import React, { useState, useRef, useEffect } from 'react';
import { usePageContent } from '../../hooks/usePageContent';
import useMeta from '../../hooks/useMeta';
import useTitle from '../../hooks/useTitle';
import { executeMutation } from '../../graphql/api';
import { subscribeToSwiftMailingList } from '../../graphql/mutations';
import styles from './index.module.css';

const GROUP_ID = 'zzzz69a8cb7abee7d461';

const DEFAULT_FLEET_OPTIONS = [
  { id: 'fleet-1-4',     value: '1-4',        label: '1-4 (minimum order: 5 sets of plates)' },
  { id: 'fleet-5-24',    value: '5-24',       label: '5-24' },
  { id: 'fleet-25-plus', value: '25 or more', label: '25+' },
];

const DEFAULT_STEPS = [
  'Get in touch with our sales team for one\u2011on\u2011one guidance on how best to design your Business plates.',
  "Send through your logo, website, business colours and branding and we'll take care of the design and provide a custom quote.",
  "Review the quote and design \u2013 and once you're happy, we'll create and deliver your Business plates. Ready to go when you are.",
];

const BusinessPlatesPage = () => {
  const { find, meta } = usePageContent('business-plates');
  const seo = meta?.seo;
  useTitle(seo?.title || 'Business plates');
  useMeta(seo);
  const c = find('businessPlates') || {};

  const fields = c.fields || {};
  const fleetOptions = c.fleetOptions || DEFAULT_FLEET_OPTIONS;
  const steps = c.steps || DEFAULT_STEPS;

  const [form, setForm] = useState({
    firstName: '', lastName: '', company: '', email: '',
    phone: '', fleetSize: '', enquiry: '',
    consentContact: false, consentMarketing: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const responseMsgRef = useRef(null);

  useEffect(() => {
    if (message && responseMsgRef.current) {
      responseMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [message]);

  const set = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setErrors(e => ({ ...e, [key]: null }));
  };

  const isValidAusPhone = (phone) => {
    const clean = phone.replace(/[\s()-]/g, '');
    return /^(?:\+?61|0)[2-478](?:[0-9]){8}$/.test(clean) && clean.length >= 10;
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = true;
    if (!form.lastName.trim()) errs.lastName = true;
    if (!form.company.trim()) errs.company = true;
    const email = form.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = true;
    const phone = form.phone.trim();
    if (!phone || !isValidAusPhone(phone)) errs.phone = true;
    if (!form.fleetSize) errs.fleetSize = true;
    if (!form.consentContact) errs.consentContact = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setStatus('error');
      setMessage('Please complete all required fields correctly.');
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const result = await executeMutation(subscribeToSwiftMailingList, {
        input: {
          firstName:   form.firstName.trim(),
          lastName:    form.lastName.trim(),
          email:       form.email.trim(),
          groupId:     c.groupId || GROUP_ID,
          phone:       form.phone.trim() || undefined,
          extraFields: [
            { key: 'COMPANY',           value: form.company.trim(),                   swiftPrefix: 'cu' },
            { key: 'FLEET_SIZE',        value: form.fleetSize,                        swiftPrefix: 'cu' },
            { key: 'ENQUIRY',           value: form.enquiry.trim(),                   swiftPrefix: 'cu' },
            { key: 'CONSENT_CONTACT',   value: form.consentContact   ? 'Yes' : 'No', swiftPrefix: 'cu' },
            { key: 'CONSENT_MARKETING', value: form.consentMarketing ? 'Yes' : 'No', swiftPrefix: 'cu' },
          ],
        },
      });

      const res = result?.subscribeToSwiftMailingList;
      if (res?.success) {
        setStatus('success');
        setMessage(c.successMessage || 'Thank you! Our sales team will be in touch shortly.');
        setForm({ firstName: '', lastName: '', company: '', email: '', phone: '', fleetSize: '', enquiry: '', consentContact: false, consentMarketing: false });
      } else {
        setStatus('error');
        setMessage(res?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  const fg = (field) =>
    `${styles['form-group']}${errors[field] ? ' ' + styles['has-error'] : ''}`;

  return (
    <div id="content" className={styles.page}>
      <div className="page-header">
        <div className="container">
          <h1 className="page-header__title">{c.pageTitle || 'Business plates'}</h1>
        </div>
      </div>

      <div className="content-holder content-holder--indented">
        <div className="row column-splitter">
        <div className="col-xs-6">
        <div className="rich-text">
        <p>
          <img
            alt={c.heroImageAlt || 'Fleet of utes with branded business plates'}
            src={c.heroImageSrc || '/images/content/business-plates-hero.png'}
            className={styles.heroImg}
          />
        </p>
        <h2>{c.headline1 || 'Drive enquiries. Build awareness. Grow your business.'}</h2>
        <p>
          {c.body1 || 'With your logo, website and branding on display, Business plates help put your business in front of more people, more often. Whether your team is travelling between jobs, meeting clients or simply parked at a site, your vehicles will be working for you – wherever the road takes you.'}
        </p>
        <p><strong>{c.salesCtaText || 'Talk to our sales team about branded Business plates.'}</strong></p>
        <p>
          <a
            rel="noopener noreferrer"
            href={c.enquireNowHref || '#Enquiry'}
            className={styles['button-black']}
            id="button-enquire1"
          >
            {c.enquireNowText || 'Enquire now'}
          </a>
        </p>
        <p>&nbsp;</p>
        <h2>{c.headline2 || 'Getting started is as easy as 1-2-3'}</h2>
        <p>{c.intro2 || "From choosing your design to adding your branding, we'll guide you every step of the way."}</p>
        <ol>
          {steps.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
        <p>
          {c.body2 || "Whether you're a small business or a large organisation, Business plates provide a consistent, professional presence \u2013 wherever your team operates. It's a simple way to show up with confidence, every time your fleet hits the road."}
        </p>
        <img
          className={styles.mockupImg}
          alt={c.mockupImageAlt || 'Image showing which parts of a business number plate can be customised'}
          src={c.mockupImageSrc || '/images/content/Business Plates mockup.png'}
        />
        <br />
        <br />
        <table className={styles.testimonialTable}>
          <tbody>
            <tr>
              <td className={styles.testimonialCell}>
                <h3 className={styles.testimonialHeading}>
                  {c.testimonialTitle || "Building a brand that's impossible to miss"}
                </h3>
                <p className={styles.testimonialText}>
                  {c.testimonialBody || 'For Advanced Contracting Security, Business plates were "a great way to showcase our new brand professionally".'}<br />
                  <em>{c.testimonialAuthor || 'Andrew Carabott, ACS Founder'}</em>
                </p>
                <p className={styles.testimonialText}>
                  <a
                    rel="noopener noreferrer"
                    className="button"
                    href={c.caseStudyUrl || '/news-and-events/business-plates-acs'}
                    target="_blank"
                    id="button-casestudy"
                  >
                    {c.caseStudyText || 'Read case study'}
                  </a>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        </div>{/* rich-text */}
        <p id="Enquiry">&nbsp;</p>

        <div className={styles['business-plates-form-section']}>
          <h3 className={styles.formHeading}>
            {c.formSectionHeading || 'Talk to our sales team about how branded Business plates can work for your fleet.'}
          </h3>
          <p className={styles.formSubheading}>
            <em><sup>{c.formSectionSubheading || 'Complete the form and our team will be in touch. Minimum order is currently 5 sets of plates.'}</sup></em>
          </p>

          <form id="business-plates-form" autoComplete="off" onSubmit={handleSubmit}>

            <div className={`${fg('firstName')} ${styles.required}`}>
              <label htmlFor="st_FIRST_NAME">{fields.firstName?.label || 'First name'}</label>
              <input
                id="st_FIRST_NAME" name="st[FIRST_NAME]" type="text"
                value={form.firstName}
                onChange={e => set('firstName', e.target.value)}
              />
              {errors.firstName && <div className={styles['error-message']}>{fields.firstName?.error || 'Please enter your first name'}</div>}
            </div>

            <div className={`${fg('lastName')} ${styles.required}`}>
              <label htmlFor="st_LAST_NAME">{fields.lastName?.label || 'Last name'}</label>
              <input
                id="st_LAST_NAME" name="st[LAST_NAME]" type="text"
                value={form.lastName}
                onChange={e => set('lastName', e.target.value)}
              />
              {errors.lastName && <div className={styles['error-message']}>{fields.lastName?.error || 'Please enter your last name'}</div>}
            </div>

            <div className={`${fg('company')} ${styles.required}`}>
              <label htmlFor="st_COMPANY">{fields.company?.label || 'Company'}</label>
              <input
                id="st_COMPANY" name="st[COMPANY]" type="text"
                value={form.company}
                onChange={e => set('company', e.target.value)}
              />
              {errors.company && <div className={styles['error-message']}>{fields.company?.error || 'Please enter your company name'}</div>}
            </div>

            <div className={`${fg('email')} ${styles.required}`}>
              <label htmlFor="st_EMAIL">{fields.email?.label || 'Email address'}</label>
              <input
                id="st_EMAIL" name="st[EMAIL]" type="email"
                value={form.email}
                onChange={e => set('email', e.target.value)}
              />
              {errors.email && <div className={styles['error-message']}>{fields.email?.error || 'Please enter a valid email address'}</div>}
            </div>

            <div className={`${fg('phone')} ${styles.required}`}>
              <label htmlFor="st_PHONE">{fields.phone?.label || 'Phone number'}</label>
              <input
                id="st_PHONE" name="st[PHONE]" type="tel"
                placeholder={fields.phone?.placeholder || 'e.g. 0400 000 000'}
                value={form.phone}
                onChange={e => set('phone', e.target.value)}
              />
              {errors.phone && <div className={styles['error-message']}>{fields.phone?.error || 'Please enter a valid phone number'}</div>}
            </div>

            <div className={`${fg('fleetSize')} ${styles.required}`}>
              <label>{fields.fleetSize?.label || 'Fleet size'}</label>
              <div className={styles['radio-group']}>
                {fleetOptions.map((opt) => (
                  <div key={opt.id || opt.value} className={styles['radio-option']}>
                    <input
                      type="radio" id={opt.id || `fleet-${opt.value}`} name="st[FLEET_SIZE]" value={opt.value}
                      checked={form.fleetSize === opt.value}
                      onChange={() => set('fleetSize', opt.value)}
                    />
                    <label htmlFor={opt.id || `fleet-${opt.value}`}>{opt.label}</label>
                  </div>
                ))}
              </div>
              {errors.fleetSize && <div className={styles['error-message']}>{fields.fleetSize?.error || 'Please select your fleet size'}</div>}
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="st_ENQUIRY">{fields.enquiry?.label || 'Your enquiry'}</label>
              <textarea
                id="st_ENQUIRY"
                name="st[ENQUIRY]"
                placeholder={fields.enquiry?.placeholder || 'Tell us about your branded Business plates requirements...'}
                value={form.enquiry}
                onChange={e => set('enquiry', e.target.value)}
              />
            </div>

            <div className={`${fg('consentContact')} ${styles.required}`}>
              <div className={styles['checkbox-group']}>
                <div className={styles['checkbox-option']}>
                  <input
                    type="checkbox" id="consent-contact" name="consent_contact" value="yes"
                    checked={form.consentContact}
                    onChange={e => set('consentContact', e.target.checked)}
                  />
                  <label htmlFor="consent-contact">
                    {c.consentContactLabel || 'I consent to being contacted directly by the VicRoads Custom Plates sales team by email and phone - including direct phone calls - to receive personalised communications about Business plates and business related products.'}
                  </label>
                </div>
                <div className={styles['checkbox-option']}>
                  <input
                    type="checkbox" id="consent-marketing" name="consent_marketing" value="yes"
                    checked={form.consentMarketing}
                    onChange={e => set('consentMarketing', e.target.checked)}
                  />
                  <label htmlFor="consent-marketing">
                    {c.consentMarketingLabel || 'I agree to receive news and promotions about Business plates and other custom plates products, business-related products, and related partner products.'}
                  </label>
                </div>
              </div>
              {errors.consentContact && <div className={styles['error-message']}>{c.consentError || 'Please accept the contact consent to proceed'}</div>}
            </div>

            {c.privacyNoticeHtml
              ? <div className={styles['privacy-notice']} dangerouslySetInnerHTML={{ __html: c.privacyNoticeHtml }} />
              : (
                <div className={styles['privacy-notice']}>
                  Please read our{' '}
                  <a rel="noopener noreferrer" href="/privacy-collection-notice" id="link-collectionnotice" target="_blank">
                    Collection Notice
                  </a>
                  {' '}and{' '}
                  <a rel="noopener noreferrer" href="/privacy" id="link-privacy" target="_blank">
                    Privacy Policy
                  </a>
                  {' '}for information about how we handle your personal information.
                </div>
              )
            }

            <button
              type="submit"
              className={styles['btn-submit']}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Submitting...' : (c.submitText || 'Submit Enquiry')}
            </button>

            <div id="business-response-msg" ref={responseMsgRef}>
              {message && (
                <div className={`${styles['response-msg']} ${styles[status] || ''}`}>
                  {message}
                </div>
              )}
            </div>
          </form>
        </div>
        </div>{/* col-xs-6 */}
        <div className="col-xs-6" />
        </div>{/* row column-splitter */}
      </div>
    </div>
  );
};

export default BusinessPlatesPage;
